import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Shield, Zap, Brain, Lock, Users, BarChart3 } from "lucide-react";
import fissionLogo from "@/public/fission_logo_1.png";

export function Landing() {
  const features = [
    {
      icon: <Brain className="h-8 w-8" />,
      title: "AI-Powered Applications",
      description: "Access a suite of intelligent GenAI applications designed to enhance your workflow.",
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Secure & Governed",
      description: "Enterprise-grade security with AWS Cognito authentication and permission-based access.",
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: "Lightning Fast",
      description: "Optimized microfrontend architecture for instant loading and seamless performance.",
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Role-Based Access",
      description: "Apps are automatically filtered based on your permissions and user groups.",
    },
    {
      icon: <BarChart3 className="h-8 w-8" />,
      title: "Scalable Platform",
      description: "Built for growth with modular architecture that scales with your needs.",
    },
    {
      icon: <Sparkles className="h-8 w-8" />,
      title: "Modern UI",
      description: "Beautiful, responsive interface with intuitive navigation and design.",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-sales-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="mt-20 flex justify-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary-400 to-sales-400 rounded-full blur-2xl opacity-20 animate-pulse-slow"></div>
              <div className="relative  rounded-2xl p-6">
                <img
                  src={fissionLogo}
                  alt="Fission AI Portal logo"
                  className="h-16 w-16 object-contain"
                />
              </div>
            </div>
          </div>
          <div className="text-center animate-fade-in">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
              <span className="gradient-text">Fission AI Portal</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Unlock the power of artificial intelligence with our comprehensive suite of
              <span className="font-semibold text-primary-700"> intelligent applications</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/auth/login"
                className="btn-primary text-lg px-8 py-4"
              >
                Get Started
                <ArrowRight className="h-5 w-5" />
              </Link>
              <a
                href="#features"
                className="btn-secondary text-lg px-8 py-4"
              >
                Learn More
              </a>
            </div>
          </div>

          {/* Decorative Elements */}
          
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="gradient-text">Powerful Features</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to leverage AI in your daily workflow
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="card hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="text-primary-500 mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-sales-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="gradient-text">How It Works</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-sales-500 text-white text-2xl font-bold mb-4 shadow-lg">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Sign In</h3>
              <p className="text-gray-600">
                Authenticate securely with AWS Cognito using your credentials
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-sales-500 text-white text-2xl font-bold mb-4 shadow-lg">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Access Apps</h3>
              <p className="text-gray-600">
                View and access applications based on your permissions and user groups
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-sales-500 text-white text-2xl font-bold mb-4 shadow-lg">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Get Started</h3>
              <p className="text-gray-600">
                Start using AI-powered tools to enhance your productivity
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="card bg-gradient-to-br from-primary-500 to-sales-500 text-white">
            <Lock className="h-12 w-12 mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Sign in to access your personalized GenAI applications dashboard
            </p>
            <Link
              to="/auth/login"
              className="inline-flex items-center gap-2 bg-white text-primary-600 font-semibold py-3 px-8 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              Sign In Now
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4">Fission AI Portal</h3>
            <p className="text-gray-400 mb-4">
              Empowering teams with intelligent AI applications
            </p>
            <div className="flex justify-center gap-6">
              <Link
                to="/auth/login"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Sign In
              </Link>
            </div>
            <div className="mt-8 pt-8 border-t border-gray-800 text-sm text-gray-500">
              <p>&copy; {new Date().getFullYear()} Fission AI Portal. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
